import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";

import { Actions as AppActions, initializeApp } from "@app/store/app/actions";
import {
  fetchHeatMapData,
  HeatMapActions,
  updateHeatMapActivityTypes,
  updateHeatMapAdditionalFilters,
  updateHeatMapDateRange,
  updateHeatMapFunnels,
  updateHeatMapOpenStages,
  updateHeatMapRange,
  updateHeatMapStageTypes,
  updateHeatMapType,
  updateHeatMapUsers,
} from "@app/store/mapLayers/heatMap/actions";
import HeatMapLayerData from "@app/store/mapLayers/heatMap/HeatMapLayerData";
import { parseApiDate } from "@app/util/parsers";

export const heatMapLayerInitialState: HeatMapLayerData = {
  activityAdditionalFilters: {},
  activityTypeIds: [],
  dealAdditionalFilters: {},
  funnelIds: [],
  openStageIds: [],
  pins: [],
  range: { max: 0, min: 0 },
  stageTypes: [DealStageType.WON, DealStageType.LOST, DealStageType.DEFAULT],
  type: EntityType.ACTIVITY,
  userIds: [],
  visible: false,
};

const heatMapLayer = createReducer<HeatMapLayerData, AppActions | HeatMapActions>(
  heatMapLayerInitialState
)
  .handleAction(initializeApp.success, (state, { payload }) => {
    const settings = payload.metaData?.heatMapSettings;
    if (!settings) {
      return state;
    }
    return {
      ...state,
      ...settings,
      ...(settings.dateRange
        ? {
            dateRange: {
              endDate: parseApiDate(settings.dateRange.endDate),
              startDate: parseApiDate(settings.dateRange.startDate),
            },
          }
        : {}),
    };
  })
  .handleAction(fetchHeatMapData.success, (state, { payload }) => ({
    ...state,
    pins: payload,
  }))
  .handleAction(updateHeatMapDateRange, (state, { payload }) => ({
    ...state,
    ...payload,
    pins: [],
  }))
  .handleAction(updateHeatMapType, (state, { payload }) => ({
    ...state,
    pins: [],
    type: payload,
  }))
  .handleAction(updateHeatMapActivityTypes, (state, { payload }) => ({
    ...state,
    activityTypeIds: payload,
    // do not reset pins if current heat map type is not activities
    pins: state.type === EntityType.ACTIVITY ? [] : state.pins,
  }))
  .handleAction(updateHeatMapFunnels, (state, { payload }) => ({
    ...state,
    funnelIds: payload,
    // do not reset pins if current heat map type is not deals
    pins: state.type === EntityType.DEAL ? [] : state.pins,
  }))
  .handleAction(updateHeatMapStageTypes, (state, { payload }) => ({
    ...state,
    // reset selected open stages when Default Stage type is unselected
    openStageIds: !payload.includes(DealStageType.DEFAULT) ? [] : state.openStageIds,
    // do not reset pins if current heat map type is not deals
    pins: state.type === EntityType.DEAL ? [] : state.pins,
    stageTypes: payload,
  }))
  .handleAction(updateHeatMapOpenStages, (state, { payload }) => ({
    ...state,
    openStageIds: payload,
    // do not reset pins if current heat map type is not deals
    pins: state.type === EntityType.DEAL ? [] : state.pins,
  }))
  .handleAction(updateHeatMapRange, (state, { payload }) => ({
    ...state,
    range: payload,
  }))
  .handleAction(updateHeatMapUsers, (state, { payload }) => ({
    ...state,
    userIds: payload,
  }))
  .handleAction(
    updateHeatMapAdditionalFilters,
    (state, { payload: { additionalFilters, type } }) => ({
      ...state,
      activityAdditionalFilters:
        type === EntityType.ACTIVITY ? additionalFilters : state.activityAdditionalFilters,
      dealAdditionalFilters:
        type === EntityType.DEAL ? additionalFilters : state.dealAdditionalFilters,
    })
  );

export default heatMapLayer;
